/* ============================================================
   v2 table columns — ordered to match the design brief, not the
   classic grid. GDELT: ★ · IMPACT · LATEST MEMBER TITLE · DISEASE
   · LOCATION · SOURCE · ACTIVE DATE RANGE · LAST PUBLISHED. NWSS:
   ★ · STATUS · DISEASE · LOCATION · DETECTIONS · SITES · ACTIVE
   DATE RANGE · LAST DETECTION. Data routes through the same
   `BiothreatSignal` fields the classic grid uses.
   ============================================================ */
import { Box, Tooltip, Typography } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { formatDateOnly, formatLocalDateTime } from "../../../utils/dates"
import { extraColors } from "../../../utils/theme"
import { formatBiothreatActiveDateRange } from "../biothreatActiveDateRange"
import {
  ClusterMembersTooltip,
  getPrecomputedActionColumn,
  NwssSourceTooltip,
} from "../PrecomputedSharedColumns"
import {
  classifyImpact,
  getDiseaseLabel,
  getLocationLabel,
  getNwssStatus,
} from "./biothreatV2Signal"
import {
  V2_BODY_FONT_SIZE,
  V2_EYEBROW,
  V2_FONT_MONO,
  V2_META_FONT_SIZE,
  V2_NUMERIC,
  V2_ON_ACCENT,
  V2_SEVERITY_COLOR,
  V2_STATUS_COLOR,
  V2_STATUS_LABEL,
  V2_SURFACE,
} from "./biothreatV2Tokens"

/** Command-console popover styling for the source/sites hover tooltips. */
const commandTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: V2_SURFACE.panelSolid,
      border: `1px solid ${V2_SURFACE.hairlineStrong}`,
      boxShadow: "0 18px 50px rgba(0, 0, 0, 0.6)",
      p: 0,
      maxWidth: 460,
    },
  },
} as const

const Cell: React.FC<{
  children: React.ReactNode
  align?: "flex-start" | "center"
}> = ({ children, align = "flex-start" }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: align,
      height: "100%",
      width: "100%",
      minWidth: 0,
    }}
  >
    {children}
  </Box>
)

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ ...V2_EYEBROW, color: extraColors.translucent50, mt: 0.25 }}
  >
    {children}
  </Typography>
)

const Primary: React.FC<{ children: React.ReactNode; nowrap?: boolean }> = ({
  children,
  nowrap,
}) => (
  <Typography
    sx={{
      fontSize: V2_BODY_FONT_SIZE,
      fontWeight: 600,
      color: extraColors.white,
      ...(nowrap
        ? {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }
        : {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.25,
          }),
    }}
  >
    {children}
  </Typography>
)

const Numeric: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ ...V2_NUMERIC, fontSize: "0.95rem", color: extraColors.white }}
  >
    {children}
  </Typography>
)

const impactColumn: GridColDef<BiothreatSignal> = {
  field: "impact_score",
  headerName: "IMPACT",
  width: 96,
  align: "center",
  headerAlign: "center",
  type: "number",
  renderCell: ({ row }) => {
    const score = row.impact_score
    if (score == null) return null
    const color = V2_SEVERITY_COLOR[classifyImpact(score)]
    return (
      <Cell align="center">
        <Box
          sx={{
            // .impact-badge — min-width 30, height 24, radius 5, mono 12/800.
            fontFamily: V2_FONT_MONO,
            fontVariantNumeric: "tabular-nums",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 32,
            height: 24,
            px: "7px",
            borderRadius: "5px",
            fontSize: 13,
            fontWeight: 800,
            color: V2_ON_ACCENT,
            backgroundColor: color,
          }}
        >
          {score}
        </Box>
      </Cell>
    )
  },
}

const diseaseColumn: GridColDef<BiothreatSignal> = {
  field: "disease",
  headerName: "DISEASE",
  width: 150,
  align: "left",
  headerAlign: "left",
  valueGetter: (_, row) => getDiseaseLabel(row),
  renderCell: ({ row }) => (
    <Cell>
      <Primary nowrap>
        <Box component="span" sx={{ textTransform: "capitalize" }}>
          {getDiseaseLabel(row)}
        </Box>
      </Primary>
    </Cell>
  ),
}

const locationColumn = (width = 150): GridColDef<BiothreatSignal> => ({
  field: "resolved_location",
  headerName: "LOCATION",
  width,
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => {
    const geoLevel =
      row.source === "nwss"
        ? row.nwss_meta?.aggregation
        : row.gdelt_meta?.geo_level
    return (
      <Cell>
        <Primary nowrap>{getLocationLabel(row)}</Primary>
        {geoLevel ? <Sub>{geoLevel}</Sub> : null}
      </Cell>
    )
  },
})

const activeDateRangeColumn: GridColDef<BiothreatSignal> = {
  field: "active_date_range",
  headerName: "ACTIVE DATE RANGE",
  width: 180,
  sortable: false,
  align: "left",
  headerAlign: "left",
  valueGetter: (_, row) => formatBiothreatActiveDateRange(row),
  renderCell: ({ row }) => (
    <Cell>
      <Typography
        sx={{
          ...V2_NUMERIC,
          fontWeight: 600,
          fontSize: V2_META_FONT_SIZE,
          color: extraColors.white,
        }}
      >
        {formatBiothreatActiveDateRange(row)}
      </Typography>
    </Cell>
  ),
}

const lastDateColumn = (headerName: string): GridColDef<BiothreatSignal> => ({
  field: "publish_time",
  headerName,
  width: 140,
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => (
    <Cell>
      <Typography
        sx={{
          ...V2_NUMERIC,
          fontWeight: 600,
          fontSize: V2_META_FONT_SIZE,
          color: extraColors.translucent50,
        }}
      >
        {row.publish_time
          ? row.source === "nwss"
            ? formatDateOnly(row.publish_time)
            : formatLocalDateTime(row.publish_time)
          : ""}
      </Typography>
    </Cell>
  ),
})

const titleColumn: GridColDef<BiothreatSignal> = {
  field: "title",
  headerName: "LATEST MEMBER TITLE",
  flex: 1,
  minWidth: 280,
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => (
    <Cell>
      <Primary>{row.title}</Primary>
    </Cell>
  ),
}

const sourceColumn: GridColDef<BiothreatSignal> = {
  field: "cluster_count",
  headerName: "SOURCE",
  width: 96,
  align: "center",
  headerAlign: "center",
  type: "number",
  renderCell: ({ row }) => {
    const content = (
      <Cell align="center">
        <Numeric>{row.cluster_count}</Numeric>
        <Sub>articles</Sub>
      </Cell>
    )
    if (row.cluster_count <= 0) return content
    return (
      <Tooltip
        arrow
        placement="right"
        slotProps={commandTooltipSlotProps}
        title={
          <ClusterMembersTooltip
            clusterCount={row.cluster_count}
            members={row.gdelt_meta?.most_recent_members ?? []}
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            cursor: "help",
          }}
        >
          {content}
        </Box>
      </Tooltip>
    )
  },
}

const statusColumn: GridColDef<BiothreatSignal> = {
  field: "status",
  headerName: "STATUS",
  width: 120,
  sortable: false,
  align: "left",
  headerAlign: "left",
  renderCell: ({ row }) => {
    const status = getNwssStatus(row)
    if (!status) return null
    const color = V2_STATUS_COLOR[status]
    return (
      <Cell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: color,
              boxShadow: `0 0 6px ${color}`,
            }}
          />
          <Typography sx={{ ...V2_EYEBROW, color }}>
            {V2_STATUS_LABEL[status]}
          </Typography>
        </Box>
      </Cell>
    )
  },
}

const detectionsColumn: GridColDef<BiothreatSignal> = {
  field: "cluster_count",
  headerName: "DETECTIONS",
  width: 110,
  align: "center",
  headerAlign: "center",
  type: "number",
  renderCell: ({ row }) => (
    <Cell align="center">
      <Numeric>{row.nwss_meta?.n_detections ?? 0}</Numeric>
      <Sub>events</Sub>
    </Cell>
  ),
}

const sitesColumn: GridColDef<BiothreatSignal> = {
  field: "sites",
  headerName: "SITES",
  width: 80,
  sortable: false,
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => {
    const content = (
      <Cell align="center">
        <Numeric>{row.nwss_meta?.n_sites ?? 0}</Numeric>
      </Cell>
    )
    if (!row.nwss_meta || row.cluster_count <= 0) return content
    return (
      <Tooltip
        arrow
        placement="right"
        slotProps={commandTooltipSlotProps}
        title={
          <NwssSourceTooltip
            meta={row.nwss_meta}
            sourceCount={row.cluster_count}
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            cursor: "help",
          }}
        >
          {content}
        </Box>
      </Tooltip>
    )
  },
}

export const getBiothreatV2GdeltColumns = ({
  pinnedIds,
  onPin,
}: {
  pinnedIds: Set<string>
  onPin: (row: BiothreatSignal) => void
}): GridColDef<BiothreatSignal>[] => [
  getPrecomputedActionColumn({ onPin, pinnedIds }),
  impactColumn,
  titleColumn,
  diseaseColumn,
  locationColumn(140),
  sourceColumn,
  activeDateRangeColumn,
  lastDateColumn("LAST PUBLISHED"),
]

export const getBiothreatV2NwssColumns = ({
  pinnedIds,
  onPin,
}: {
  pinnedIds: Set<string>
  onPin: (row: BiothreatSignal) => void
}): GridColDef<BiothreatSignal>[] => [
  getPrecomputedActionColumn({ onPin, pinnedIds }),
  statusColumn,
  diseaseColumn,
  locationColumn(160),
  detectionsColumn,
  sitesColumn,
  activeDateRangeColumn,
  lastDateColumn("LAST DETECTION"),
]
